import { useEffect, useState } from "react";
import { Search } from "lucide-react";
import api from "../../lib/api";

export default function AdminCustomers() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const params = new URLSearchParams({ page, limit: 20 });
    if (q) params.set("q", q);
    const { data } = await api.get(`/admin/customers?${params.toString()}`);
    setItems(data.items || []);
    setTotal(data.total || 0);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, q]);

  const openDetail = async (id) => {
    const { data } = await api.get(`/admin/customers/${id}`);
    setSelected(data);
  };

  return (
    <div className="space-y-5" data-testid="admin-customers">
      <div>
        <span className="label-overline text-jow-gold">Audience</span>
        <h1 className="font-display text-3xl text-jow-navy mt-1">Customers</h1>
        <p className="text-xs text-jow-navy/55 mt-1">{total} shoppers</p>
      </div>

      <div className="relative flex-1 max-w-md">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-jow-navy/40" />
        <input
          type="text"
          placeholder="Phone / name / email"
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setPage(1);
          }}
          className="w-full border border-jow-navy/15 pl-9 pr-3 py-2 text-sm bg-white"
          data-testid="cust-search"
        />
      </div>

      <table className="w-full bg-white border border-jow-navy/10 text-sm">
        <thead className="bg-jow-navy/5 text-jow-navy/70 uppercase tracking-widest text-[10px]">
          <tr>
            <th className="text-left px-4 py-3">Customer</th>
            <th className="text-left px-4 py-3">Phone</th>
            <th className="text-right px-4 py-3">Orders</th>
            <th className="text-right px-4 py-3">LTV</th>
            <th className="px-4 py-3"></th>
          </tr>
        </thead>
        <tbody>
          {items.map((u) => (
            <tr
              key={u.id}
              className="border-t border-jow-navy/5 hover:bg-jow-cream/30 cursor-pointer"
              onClick={() => openDetail(u.id)}
              data-testid={`crow-${u.id}`}
            >
              <td className="px-4 py-3 text-jow-navy">
                {u.name || "—"}
                <br />
                <span className="text-[10px] text-jow-navy/50">{u.email}</span>
              </td>
              <td className="px-4 py-3 text-jow-navy/70">{u.phone}</td>
              <td className="px-4 py-3 text-right">{u.order_count}</td>
              <td className="px-4 py-3 text-right font-display">
                ₹{u.ltv.toLocaleString("en-IN")}
              </td>
              <td className="px-4 py-3 text-right">
                <button className="text-jow-pink text-xs">open</button>
              </td>
            </tr>
          ))}
          {items.length === 0 && (
            <tr>
              <td colSpan={5} className="text-center py-8 text-jow-navy/50">
                No customers match.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="flex items-center justify-between text-xs text-jow-navy/55">
        <span>
          Page {page} of {Math.max(1, Math.ceil(total / 20))}
        </span>
        <div className="flex gap-2">
          <button
            disabled={page <= 1}
            onClick={() => setPage((p) => p - 1)}
            className="btn-secondary-jow text-[10px] disabled:opacity-30"
            data-testid="prev-page"
          >
            Prev
          </button>
          <button
            disabled={page * 20 >= total}
            onClick={() => setPage((p) => p + 1)}
            className="btn-secondary-jow text-[10px] disabled:opacity-30"
            data-testid="next-page"
          >
            Next
          </button>
        </div>
      </div>

      {selected && (
        <div
          className="fixed inset-0 z-40 bg-jow-navy/50 flex justify-end"
          onClick={() => setSelected(null)}
          data-testid="cust-detail"
        >
          <aside
            className="w-full max-w-md bg-white h-full overflow-y-auto p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button onClick={() => setSelected(null)} className="text-xs text-jow-pink">
              ← Close
            </button>
            <div>
              <span className="label-overline text-jow-gold">Customer</span>
              <h2 className="font-display text-2xl text-jow-navy mt-1">
                {selected.user.name || selected.user.phone}
              </h2>
              <p className="text-xs text-jow-navy/55">
                {selected.user.email || "—"} · joined {new Date(selected.user.created_at).toLocaleDateString()}
              </p>
            </div>
            <div className="grid grid-cols-3 gap-2 text-center">
              <Mini label="Orders" value={selected.orders.length} />
              <Mini label="LTV" value={`₹${selected.ltv.toLocaleString("en-IN")}`} />
              <Mini label="Wishlist" value={selected.wishlist_count} />
            </div>
            <div>
              <p className="label-overline text-jow-navy mb-2">Recent orders</p>
              <ul className="space-y-2">
                {selected.orders.slice(0, 20).map((o) => (
                  <li key={o.order_no} className="text-sm flex justify-between border-b border-jow-navy/5 py-1">
                    <span className="text-jow-navy">{o.order_no}</span>
                    <span className="text-jow-navy/60">
                      {o.status} · ₹{(o.total || 0).toLocaleString("en-IN")}
                    </span>
                  </li>
                ))}
                {selected.orders.length === 0 && (
                  <li className="text-xs text-jow-navy/50">No orders yet.</li>
                )}
              </ul>
            </div>
            <div>
              <p className="label-overline text-jow-navy mb-2">Addresses</p>
              <ul className="space-y-2">
                {selected.addresses.map((a) => (
                  <li key={a.id} className="text-xs text-jow-navy/70">
                    {a.line1}, {a.city} {a.pincode}
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-jow-cream/50 border border-jow-navy/10 p-3">
      <p className="label-overline text-[9px] text-jow-navy/50">{label}</p>
      <p className="font-display text-xl text-jow-navy mt-0.5">{value}</p>
    </div>
  );
}
